/**
 * Traducción entre las entidades del dominio y la forma plana de una fila.
 *
 * Casi todas las tablas mapean 1:1, así que el mapeo por defecto es la identidad. Los dos
 * casos que no: `Ejercicio.rango_reps` es una tupla y en la planilla son dos columnas (más
 * legible a mano que "10|12"), y el perfil suma preferencias que no están en `Usuario`.
 */

import type {
  Ejercicio,
  Gimnasio,
  GrupoMuscular,
  Musculo,
  Patron,
  Sexo,
  TipoEjercicio,
  Unidad,
  Usuario,
} from '@mg/core';

export interface Perfil extends Usuario {
  /** Objetivo de series semanales por grupo muscular (§5.2). */
  objetivo_series_semanales: Partial<Record<GrupoMuscular, number>>;
  formula_e1rm: 'epley' | 'brzycki';
  /**
   * Objetivos de nutrición. Los pone el usuario y pueden quedar en null: §10 prohíbe que la app
   * prescriba números de calorías, así que sin objetivo escrito no hay objetivo.
   */
  objetivo_proteina_g: number | null;
  objetivo_kcal: number | null;
}

type Fila = Record<string, unknown>;

// ---------------------------------------------------------------------------

export function ejercicioDeFila(f: Fila): Ejercicio {
  const min = f['rango_reps_min'] as number | null;
  const max = f['rango_reps_max'] as number | null;
  return {
    id: String(f['id'] ?? ''),
    nombre: String(f['nombre'] ?? ''),
    musculo_primario: f['musculo_primario'] as Musculo,
    musculos_secundarios: (f['musculos_secundarios'] as string[]) as Musculo[],
    tipo: f['tipo'] as TipoEjercicio,
    patron: f['patron'] as Patron,
    unilateral: Boolean(f['unilateral']),
    es_global: Boolean(f['es_global']),
    creado_por_user_id: (f['creado_por_user_id'] as string | null) ?? null,
    rango_reps: min !== null && max !== null ? [min, max] : null,
  };
}

export function ejercicioAFila(e: Ejercicio): Fila {
  return {
    id: e.id,
    nombre: e.nombre,
    musculo_primario: e.musculo_primario,
    musculos_secundarios: e.musculos_secundarios,
    tipo: e.tipo,
    patron: e.patron,
    unilateral: e.unilateral,
    es_global: e.es_global,
    creado_por_user_id: e.creado_por_user_id,
    rango_reps_min: e.rango_reps?.[0] ?? null,
    rango_reps_max: e.rango_reps?.[1] ?? null,
  };
}

// ---------------------------------------------------------------------------

export function perfilDeFila(f: Fila): Perfil {
  let objetivo: Partial<Record<GrupoMuscular, number>> = {};
  const crudo = f['objetivo_series_semanales'];
  if (typeof crudo === 'string' && crudo.trim() !== '') {
    try {
      const parseado = JSON.parse(crudo) as unknown;
      if (parseado && typeof parseado === 'object' && !Array.isArray(parseado)) {
        objetivo = parseado as Partial<Record<GrupoMuscular, number>>;
      }
    } catch {
      // Una celda que el usuario editó mal no debe romper el login: se ignora el objetivo.
      objetivo = {};
    }
  }

  const formula = f['formula_e1rm'];
  return {
    id: String(f['id'] ?? ''),
    nombre: String(f['nombre'] ?? ''),
    fecha_nacimiento: (f['fecha_nacimiento'] as string | null) ?? null,
    sexo: (f['sexo'] as Sexo) || 'otro',
    altura_cm: (f['altura_cm'] as number | null) ?? null,
    unidad_preferida: ((f['unidad_preferida'] as Unidad) || 'kg') as Unidad,
    zona_horaria: String(f['zona_horaria'] ?? 'America/Santiago'),
    objetivo_series_semanales: objetivo,
    formula_e1rm: formula === 'brzycki' ? 'brzycki' : 'epley',
    objetivo_proteina_g: (f['objetivo_proteina_g'] as number | null) ?? null,
    objetivo_kcal: (f['objetivo_kcal'] as number | null) ?? null,
  };
}

export function perfilAFila(p: Perfil): Fila {
  return {
    id: p.id,
    nombre: p.nombre,
    fecha_nacimiento: p.fecha_nacimiento,
    sexo: p.sexo,
    altura_cm: p.altura_cm,
    unidad_preferida: p.unidad_preferida,
    zona_horaria: p.zona_horaria,
    objetivo_series_semanales:
      Object.keys(p.objetivo_series_semanales).length > 0
        ? JSON.stringify(p.objetivo_series_semanales)
        : null,
    formula_e1rm: p.formula_e1rm,
    objetivo_proteina_g: p.objetivo_proteina_g,
    objetivo_kcal: p.objetivo_kcal,
  };
}

// ---------------------------------------------------------------------------

export interface Cuenta {
  id: string;
  email: string;
  hash_clave: string;
  planilla_id: string;
  creado_en: string;
}

export interface Mapeo<T> {
  deFila: (f: Fila) => T;
  aFila: (t: T) => Fila;
}

/** Para las tablas cuya entidad coincide con la fila. */
export function identidad<T>(): Mapeo<T> {
  return {
    deFila: (f) => f as T,
    aFila: (t) => t as Fila,
  };
}

/**
 * Los días de cierre se guardan como lista de texto ("0" o "0|6") porque es lo legible en una
 * celda, pero el motor de calendario los compara contra el número de día de la semana. Sin
 * esta conversión `new Set(['0']).has(0)` da false y el gimnasio aparecería abierto los
 * domingos, sin ningún error visible: el colchón simplemente saldría mal.
 */
export function gimnasioDeFila(f: Fila): Gimnasio {
  const crudo = f['dias_cierre_semanal'];
  const lista = Array.isArray(crudo) ? crudo : typeof crudo === 'string' ? crudo.split('|') : [];
  return {
    id: String(f['id'] ?? ''),
    nombre: String(f['nombre'] ?? ''),
    notas: String(f['notas'] ?? ''),
    dias_cierre_semanal: lista
      .map((d) => Number(String(d).trim()))
      .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6),
  };
}

export function gimnasioAFila(g: Gimnasio): Fila {
  return {
    id: g.id,
    nombre: g.nombre,
    notas: g.notas,
    dias_cierre_semanal: g.dias_cierre_semanal.map(String),
  };
}

export const MAPEO_EJERCICIO: Mapeo<Ejercicio> = { deFila: ejercicioDeFila, aFila: ejercicioAFila };
export const MAPEO_PERFIL: Mapeo<Perfil> = { deFila: perfilDeFila, aFila: perfilAFila };
export const MAPEO_GIMNASIO: Mapeo<Gimnasio> = { deFila: gimnasioDeFila, aFila: gimnasioAFila };
